import React, { useState, useEffect } from 'react';
import ShipmentTable from '../components/shipments/ShipmentTable';
import ShipmentForm from '../components/shipments/ShipmentForm';
import { Plus, RefreshCw } from 'lucide-react';
import { fetchAndStoreShipments, getStoredShipments } from '../services/terminal49';
import { ShipmentData } from '../lib/supabase';

const ShipmentsPage: React.FC = () => {
  const [shipments, setShipments] = useState<ShipmentData[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingShipment, setEditingShipment] = useState<ShipmentData | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadShipments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getStoredShipments();
      setShipments(data);
    } catch (err) {
      setError('Failed to load shipments');
      console.error('Error loading shipments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshShipments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await fetchAndStoreShipments();
      await loadShipments();
    } catch (err) {
      setError('Failed to refresh shipments');
      console.error('Error refreshing shipments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadShipments();
    const interval = setInterval(refreshShipments, 30 * 60 * 1000); // Refresh every 30 minutes
    return () => clearInterval(interval);
  }, []);

  const handleView = (shipment: ShipmentData) => {
    // Implement view logic
    console.log('View shipment:', shipment);
  };

  const handleEdit = (shipment: ShipmentData) => {
    setEditingShipment(shipment);
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Shipments</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage all your shipments and track their status
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-4">
          <button
            onClick={refreshShipments}
            disabled={isLoading}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => {
              setEditingShipment(undefined);
              setShowForm(true);
            }}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Shipment
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {showForm ? (
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {editingShipment ? 'Edit Shipment' : 'New Shipment'}
          </h2>
          <ShipmentForm
            onSubmit={() => {
              setShowForm(false);
              loadShipments();
            }}
            initialData={editingShipment}
          />
        </div>
      ) : (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <ShipmentTable
              shipments={shipments}
              onView={handleView}
              onEdit={handleEdit}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentsPage;